import { FC, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Avatar,
  Box,
  Card,
  CardHeader,
  CircularProgress,
  Container,
  Grid,
  Typography,
} from '@mui/material';
import {
  type CollisionDetection,
  DndContext,
  DragEndEvent,
  DragOverEvent,
  DragOverlay,
  DragStartEvent,
  KeyboardSensor,
  PointerSensor,
  pointerWithin,
  rectIntersection,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { sortableKeyboardCoordinates } from '@dnd-kit/sortable';
import {
  Loading,
  useMuiSwitchableTheme,
  useToastr,
} from '@farmshare/ui-components';
import { useCatalogState, useQuestionnaireState } from '../hooks';
import { useCapabilityHandlers } from '../hooks/use-capability-handlers';
import { useQuestionHandlers } from '../hooks/use-question-handlers';
import {
  useCutsheetCapabilityByIdQuery,
  useCutsheetQuestionnaireByIdQuery,
} from 'lib/graphql';
import { useUserState } from 'providers/user-state-provider';
import {
  CapabilitiesPanel,
  QuestionnairePanel,
  QuestionnaireWizard,
  SaveFooter,
} from '../components';
import type { Cut, Extra, Specification, Trim } from '../types';

/**
 * Unified Cutsheet Builder
 *
 * Two-column layout with:
 * - Left: Capabilities management (Cuts, Trim, Extras, Specs)
 * - Right: Questionnaire builder with drag & drop support
 *
 * This component is a thin guard that ensures vendorId and animalType are
 * resolved before mounting the inner builder.
 */
export const UnifiedCutsheetBuilder: FC = () => {
  const [searchParams] = useSearchParams();
  const questionnaireId = searchParams.get('id');
  const { vendor } = useUserState();

  const { data: questionnaireData, loading: loadingQuestionnaire } =
    useCutsheetQuestionnaireByIdQuery({
      variables: { id: questionnaireId! },
      skip: !questionnaireId,
    });

  const capabilityId =
    questionnaireData?.cutsheetQuestionnaireById?.capabilityId;

  if (questionnaireId && loadingQuestionnaire) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="400px"
        >
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (!vendor?._id || (questionnaireId && !capabilityId)) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Typography color="error">
          Missing required information. Please ensure you have a vendor account
          {questionnaireId ? ' and the form is loaded' : ''}.
        </Typography>
      </Container>
    );
  }

  return (
    <UnifiedCutsheetBuilderInner
      vendorId={vendor._id}
      capabilityId={capabilityId!}
      questionnaireId={questionnaireId}
    />
  );
};

// ---------------------------------------------------------------------------
// Inner component — requires resolved vendorId + animalType
// ---------------------------------------------------------------------------

interface UnifiedCutsheetBuilderInnerProps {
  vendorId: string;
  capabilityId: string;
  questionnaireId: string | null;
}

const UnifiedCutsheetBuilderInner: FC<UnifiedCutsheetBuilderInnerProps> = ({
  vendorId,
  capabilityId,
  questionnaireId,
}) => {
  const [isDraggingCapability, setIsDraggingCapability] = useState(false);
  const [activeItem, setActiveItem] = useState<{
    item: Cut | Trim | Extra | Specification;
    type: 'cut' | 'trim' | 'extra' | 'specification';
  } | null>(null);
  const [showPreview, setShowPreview] = useState(false);
  const { push } = useToastr();
  const { marginLeft, marginTop } = useMuiSwitchableTheme();

  // Fetch the capability object itself (used for printSettings in the preview)
  const { data: capabilityData } = useCutsheetCapabilityByIdQuery({
    variables: { id: capabilityId },
    skip: !capabilityId,
  });

  // Capabilities state
  const {
    catalog: capabilities,
    addCut,
    updateCut,
    addTrim,
    updateTrim,
    addExtra,
    updateExtra,
    addSpecification,
    updateSpecification,
    updateCatalogInfo: updateCapabilitiesInfo,
  } = useCatalogState({ vendorId, capabilityId });

  // Questionnaire state
  const {
    questionnaire,
    loading: questionnaireLoading,
    addQuestionAtIndex,
    updateQuestion,
    removeQuestion,
    addItemsToQuestion,
    removeItemFromQuestion,
    reorderItemsInQuestion,
    updateQuestionnaireInfo,
    reorderQuestionsByIndex,
    saveQuestionnaire,
    isDirty,
    isSaving,
  } = useQuestionnaireState({
    questionnaireId: questionnaireId || undefined,
    capabilityId,
  });

  // Drag and drop sensors
  // activationConstraint requires the pointer to move 8px before a drag begins,
  // which prevents the sensor from eating pointer events on the scrollable
  // capabilities panel and allows normal scroll to work alongside dragging.
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 8 },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  // ---------------------------------------------------------------------------
  // Business logic hooks (capability + question CRUD)
  // ---------------------------------------------------------------------------

  const capabilityHandlers = useCapabilityHandlers({
    capabilities,
    capabilityId,
    addCut,
    updateCut,
    addTrim,
    updateTrim,
    addExtra,
    updateExtra,
    addSpecification,
    updateSpecification,
    updateCapabilitiesInfo,
  });

  const questionHandlers = useQuestionHandlers({
    capabilities,
    questionnaire: questionnaire!,
    capabilityId,
    capability: capabilityData?.CutsheetCapabilityById,
    addQuestionAtIndex,
    updateQuestion,
    removeQuestion,
    addItemsToQuestion,
    removeItemFromQuestion,
    reorderItemsInQuestion,
    updateQuestionnaireInfo,
    saveQuestionnaire,
  });

  // ---------------------------------------------------------------------------
  // Drag-and-drop event handlers
  // ---------------------------------------------------------------------------

  const handleDragStart = (event: DragStartEvent) => {
    const activeData = event.active.data.current;
    if (activeData?.type === 'capability') {
      setIsDraggingCapability(true);
      setActiveItem({
        item: activeData.capability,
        type: activeData.capabilityType,
      });
    }
  };

  const handleDragOver = (_event: DragOverEvent) => {
    // Reserved for future hover-highlight logic.
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    setIsDraggingCapability(false);
    setActiveItem(null);

    if (!over) return;

    const activeData = active.data.current;
    const overData = over.data.current;

    if (
      activeData?.type === 'capability' &&
      overData?.type === 'question-dropzone'
    ) {
      const questionId = (over.id as string).replace('question-dropzone-', '');
      const capability = activeData.capability as
        | Cut
        | Trim
        | Extra
        | Specification;
      if (questionId && capability.id) {
        questionHandlers.handleAddItemToQuestion(questionId, capability.id);
      }
    }

    // Drag a cut from the catalog directly onto a ChoiceGroup option dropzone
    if (
      activeData?.type === 'capability' &&
      overData?.type === 'option-dropzone'
    ) {
      const { questionId, optionId } = overData as {
        questionId: string;
        optionId: string;
        type: string;
      };
      const capability = activeData.capability as Cut;
      if (questionId && optionId && capability.id) {
        questionHandlers.handleDropCutToOption(
          questionId,
          optionId,
          capability.id,
        );
      }
    }
  };

  /** Save the current draft without changing publish state */
  const handleSave = async () => {
    try {
      await saveQuestionnaire();
      push({
        title: 'Saved',
        body: 'Questionnaire saved successfully',
        bg: 'success',
      });
    } catch {
      push({
        title: 'Error',
        body: 'Failed to save questionnaire',
        bg: 'error',
      });
    }
  };

  /** Save the current draft and set it as published */
  const handleSaveAndPublish = async () => {
    if (!questionnaire || questionnaire.questions.length === 0) {
      push({
        title: 'Cannot Publish',
        body: 'Add at least one question before publishing',
        bg: 'warning',
      });
      return;
    }
    try {
      await saveQuestionnaire({ status: 'published' });
      push({
        title: 'Published',
        body: 'Questionnaire saved and published',
        bg: 'success',
      });
    } catch {
      push({
        title: 'Error',
        body: 'Failed to save questionnaire',
        bg: 'error',
      });
    }
  };

  if (!questionnaire || questionnaireLoading) {
    return <Loading />;
  }

  /**
   * Custom collision detection: prefer the pointer tip (accurate for nested
   * droppables such as per-option dropzones inside a ChoiceGroup card) and
   * fall back to rectIntersection for keyboard navigation where there is no
   * pointer position.
   * @param args
   */
  const customCollisionDetection: CollisionDetection = (args) => {
    const pointerCollisions = pointerWithin(args);
    if (pointerCollisions.length > 0) return pointerCollisions;
    return rectIntersection(args);
  };

  return (
    <Container sx={{ pt: 10, pb: 1 }} maxWidth="xl">
      <DndContext
        sensors={sensors}
        collisionDetection={customCollisionDetection}
        onDragStart={handleDragStart}
        onDragOver={handleDragOver}
        onDragEnd={handleDragEnd}
      >
        <Grid container spacing={3} sx={{ mt: 2 }}>
          {/* Left Column - Capabilities */}
          <Grid
            size={{ xs: 12, md: 4 }}
            sx={{ display: { xs: 'none', md: 'block' } }}
          >
            <CapabilitiesPanel
              capabilities={capabilities}
              questionnaire={questionnaire}
              isDraggingCapability={isDraggingCapability}
              onAddCut={capabilityHandlers.handleAddCut}
              onEditCut={capabilityHandlers.handleEditCut}
              onAddTrim={capabilityHandlers.handleAddTrim}
              onEditTrim={capabilityHandlers.handleEditTrim}
              onAddExtra={capabilityHandlers.handleAddExtra}
              onEditExtra={capabilityHandlers.handleEditExtra}
              onAddSpecification={capabilityHandlers.handleAddSpecification}
              onEditSpecification={capabilityHandlers.handleEditSpecification}
            />
          </Grid>

          {/* Right Column - Questionnaire */}
          <Grid size={{ xs: 12, md: 8 }}>
            <QuestionnairePanel
              questionnaire={questionnaire}
              questionnaireLoading={questionnaireLoading}
              capabilities={capabilities}
              draggingType={activeItem?.type ?? null}
              onAddQuestion={questionHandlers.handleAddQuestion}
              onEditQuestion={questionHandlers.handleEditQuestion}
              onDeleteQuestion={questionHandlers.handleDeleteQuestion}
              onAddItem={questionHandlers.handleAddItemToQuestion}
              onAddItems={questionHandlers.handleAddItemsToQuestion}
              onRemoveItem={questionHandlers.handleRemoveItemFromQuestion}
              onEditItem={capabilityHandlers.handleEditItemFromQuestion}
              onAddCutToOption={questionHandlers.handleAddCutToOption}
              onRemoveCutFromOption={questionHandlers.handleRemoveCutFromOption}
              onReorderQuestions={reorderQuestionsByIndex}
              onReorderItems={questionHandlers.handleReorderItemsInQuestion}
            />
          </Grid>
        </Grid>

        {/* Drag overlay — renders a floating card while dragging a capability */}
        <DragOverlay>
          {activeItem ? (
            <Card
              variant="outlined"
              sx={{
                cursor: 'grabbing',
                border: '2px solid',
                borderColor: 'primary.main',
                boxShadow: 3,
              }}
            >
              <CardHeader
                avatar={
                  <Avatar
                    sx={{ bgcolor: 'primary.main' }}
                    src={activeItem.item.imageUrl}
                    alt={activeItem.item.title || ''}
                  />
                }
                title={activeItem.item.title || 'Unnamed'}
                subheader={
                  'price' in activeItem.item && activeItem.item.price
                    ? `$${activeItem.item.price.toFixed(2)}`
                    : undefined
                }
                titleTypographyProps={{
                  variant: 'body2',
                  fontWeight: 'medium',
                }}
                subheaderTypographyProps={{ variant: 'subtitle2' }}
              />
            </Card>
          ) : null}
        </DragOverlay>
      </DndContext>

      {/* Questionnaire Preview Dialog */}
      <QuestionnaireWizard
        open={showPreview}
        onClose={() => setShowPreview(false)}
        questionnaire={questionnaire}
        catalog={capabilities}
        printSettings={{
          primalColumns:
            capabilityData?.CutsheetCapabilityById?.printSettings
              ?.primalColumns ?? 2,
          specsLayoutPosition:
            (capabilityData?.CutsheetCapabilityById?.printSettings
              ?.specsLayoutPosition as
              | 'top'
              | 'bottom'
              | 'left'
              | 'right'
              | 'none'
              | undefined) ?? 'right',
          showPrimalGroup:
            capabilityData?.CutsheetCapabilityById?.printSettings
              ?.showPrimalGroup ?? true,
          alwaysShowPrimalNotes:
            capabilityData?.CutsheetCapabilityById?.printSettings
              ?.alwaysShowPrimalNotes ?? true,
          hiddenGroupIds: (
            capabilityData?.CutsheetCapabilityById?.printSettings
              ?.hiddenGroupIds ?? []
          ).filter((id): id is string => Boolean(id)),
          hiddenItemIds: (
            capabilityData?.CutsheetCapabilityById?.printSettings
              ?.hiddenItemIds ?? []
          ).filter((id): id is string => Boolean(id)),
        }}
      />

      {/* Sticky Save / Publish Footer */}
      <SaveFooter
        questionnaire={questionnaire}
        isDirty={isDirty}
        isSaving={isSaving}
        marginLeft={marginLeft}
        marginTop={marginTop}
        onSave={handleSave}
        onSaveAndPublish={handleSaveAndPublish}
        onOpenSettings={questionHandlers.handleEditQuestionnaireInfo}
        onOpenPreview={() => setShowPreview(true)}
      />
    </Container>
  );
};
